// Pasarela de cobro con tarjeta. POS → Configuración → Pasarela de cobro.
//
// Se configura POR MÓDULO (POS y POS Restaurant por separado) porque los casos
// no son el mismo: en el mostrador con la tarjeta delante el datáfono es más
// rápido; en la mesa o a domicilio gana el enlace.
//
// Los tres modos conviven y se encienden por separado:
//   · Datáfono — el terminal físico de siempre; se concilia después contra la
//     liquidación del adquirente. No necesita credenciales.
//   · Enlace   — la pasarela devuelve una página propia donde el cliente teclea
//     la tarjeta. El número y el CVV no pasan por este sistema.
//   · Directo  — la tarjeta se teclea aquí. Somete TODA la plataforma a PCI-DSS,
//     así que el interruptor deja registrado quién lo encendió.
//
// El token se cifra en reposo (write-only): la pantalla sólo sabe si está puesto.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CreditCard, Link2, ShieldCheck, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import {
  Campo, FormaPago, Switch, fieldCls, pos,
  type ModuloPos, type PasarelaConfig, pasarelaConfigGet, pasarelaConfigSet,
} from './shared';
import { LOCALE } from '@/lib/utils/regional';

const MODULOS: { id: ModuloPos; label: string }[] = [
  { id: 'pos', label: 'POS (mostrador)' },
  { id: 'restaurant', label: 'POS Restaurant (salón)' },
  { id: 'hotel', label: 'Hotel (recepción)' },
  { id: 'taller', label: 'Taller (mostrador)' },
];

const vacio = {
  proveedor: 'pagomedios', ambiente: 'sandbox', base_url: '', merchant_id: '',
  forma_pago_id: '', es_activo: true,
  modo_datafono: true, modo_enlace: false, modo_directo: false, modo_deuna: false,
  forma_pago_deuna_id: '',
  expira_segundos: '300', token: '',
};

export function ConfigPasarela({ moduloInicial = 'pos', soloModulo = false }: { moduloInicial?: ModuloPos; soloModulo?: boolean } = {}) {
  const qc = useQueryClient();
  const [modulo, setModulo] = useState<ModuloPos>(moduloInicial);

  const { data: todas = [] } = useQuery<PasarelaConfig[]>({
    queryKey: ['pos-pasarela-config'], queryFn: () => pasarelaConfigGet(), staleTime: 0,
  });
  const { data: formas = [] } = useQuery<FormaPago[]>({
    queryKey: ['pos-formas'],
    queryFn: () => pos.list<FormaPago>('formas_pago', '?select=*&deleted_at=is.null&order=orden'),
  });

  const cfg = todas.find((c) => c.modulo === modulo) ?? null;
  const [f, setF] = useState<typeof vacio>(vacio);
  const set = (patch: Partial<typeof vacio>) => setF((s) => ({ ...s, ...patch }));

  useEffect(() => {
    setF(cfg ? {
      proveedor: cfg.proveedor, ambiente: cfg.ambiente, base_url: cfg.base_url ?? '',
      merchant_id: cfg.merchant_id ?? '', forma_pago_id: cfg.forma_pago_id ?? '',
      es_activo: cfg.es_activo, modo_datafono: cfg.modo_datafono,
      modo_enlace: cfg.modo_enlace, modo_directo: cfg.modo_directo,
      modo_deuna: cfg.modo_deuna ?? false,
      forma_pago_deuna_id: cfg.forma_pago_deuna_id ?? '',
      expira_segundos: String(cfg.expira_segundos ?? 300), token: '',
    } : vacio);
  }, [cfg, modulo]);

  const guardar = useMutation({
    mutationFn: () => pasarelaConfigSet({
      modulo, proveedor: f.proveedor, ambiente: f.ambiente,
      base_url: f.base_url || null, merchant_id: f.merchant_id || null,
      forma_pago_id: f.forma_pago_id || null, es_activo: f.es_activo,
      modo_datafono: f.modo_datafono, modo_enlace: f.modo_enlace,
      modo_deuna: f.modo_deuna, forma_pago_deuna_id: f.forma_pago_deuna_id || null,
      modo_directo: false,   // no implementado: guardar lo deja apagado, no en falso encendido
      expira_segundos: Number(f.expira_segundos) || 300,
      token: f.token || '',        // '' = conservar el que ya está
    }),
    onSuccess: (r) => {
      toast.success(r.message, { duration: f.modo_directo ? 12000 : 4000 });
      void qc.invalidateQueries({ queryKey: ['pos-pasarela-config'] });
      set({ token: '' });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const necesitaToken = f.modo_enlace || f.modo_deuna;
  const sinToken = necesitaToken && !f.token && !cfg?.token_configurado;

  return (
    <div className="space-y-4 p-6">
      <h1 className="flex items-center gap-2 text-xl font-semibold">
        <CreditCard className="h-5 w-5 text-primary" /> Pasarela de cobro con tarjeta{soloModulo ? ` · ${MODULOS.find((m) => m.id === modulo)?.label ?? modulo}` : ''}
      </h1>

      {/* Módulo: cada uno se configura por separado (desde el Hotel se ve solo el suyo) */}
      <div className={`flex gap-2 ${soloModulo ? 'hidden' : ''}`}>
        {MODULOS.map((m) => (
          <button key={m.id} onClick={() => setModulo(m.id)}
            className={`rounded-md border px-3 py-2 text-sm ${modulo === m.id ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
            {m.label}
            {todas.find((c) => c.modulo === m.id && c.es_activo && c.modo_enlace) && (
              <span className="ml-2 rounded bg-emerald-500/20 px-1.5 text-[10px] text-emerald-700 dark:text-emerald-300">enlace</span>
            )}
          </button>
        ))}
      </div>

      {modulo === 'taller' && !cfg && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
          El taller aún no tiene su pasarela configurada: complete los datos y guarde. Es independiente de la del POS; con ella el cliente paga el anticipo desde el portal Pit Stop.
        </div>
      )}
      {modulo === 'hotel' && !cfg && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
          El hotel aún no tiene su pasarela configurada: complete los datos y guarde. Es independiente de la del POS y del Restaurant.
        </div>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Cómo se puede cobrar con tarjeta aquí</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-start gap-3">
            <Switch checked={f.modo_datafono} onChange={(v) => set({ modo_datafono: v })} label="" />
            <span className="text-sm">
              <span className="flex items-center gap-1 font-medium"><Smartphone className="h-4 w-4" /> Datáfono</span>
              <span className="text-muted-foreground">
                La tarjeta se pasa por el terminal físico y el cajero anota la autorización.
                Se concilia después con el archivo de liquidación del adquirente. No necesita credenciales.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3">
            <Switch checked={f.modo_enlace} onChange={(v) => set({ modo_enlace: v })} label="" />
            <span className="text-sm">
              <span className="flex items-center gap-1 font-medium"><Link2 className="h-4 w-4" /> Enlace de pago (QR)</span>
              <span className="text-muted-foreground">
                La pasarela devuelve una página propia; el cliente escanea el QR y paga allí.
                El número de tarjeta y el CVV nunca pasan por este sistema.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3">
            <Switch checked={f.modo_deuna} onChange={(v) => set({ modo_deuna: v })} label="" />
            <span className="text-sm">
              <span className="flex items-center gap-1 font-medium"><Link2 className="h-4 w-4" /> De Una (QR)</span>
              <span className="text-muted-foreground">
                Va por la <strong>misma pasarela</strong> que la tarjeta: mismo token, mismo enlace.
                El cliente escanea con su app De Una. Necesita la <strong>licencia De Una</strong>
                contratada con Abitmedia — sin ella el enlace falla al abrirse.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3 opacity-60">
            <Switch checked={false} onChange={() => undefined} disabled label="" />
            <span className="text-sm">
              <span className="flex items-center gap-1 font-medium">
                <AlertTriangle className="h-4 w-4 text-amber-600" /> Tarjeta directa en el POS
                <span className="ml-1 rounded bg-muted px-1.5 text-[10px] font-normal uppercase tracking-wide text-muted-foreground">
                  no disponible
                </span>
              </span>
              <span className="text-muted-foreground">
                La tarjeta se teclearía en esta pantalla. <strong>No está implementado</strong> y no lo estará
                mientras no haya certificación <strong>PCI-DSS</strong>: cifrado certificado, políticas de
                retención, pruebas de intrusión periódicas y auditoría anual. Para cobrar con tarjeta usa
                el <strong>enlace de pago</strong>: el número y el CVV nunca pasan por este sistema.
              </span>
              {cfg?.directo_habilitado_at && (
                <span className="mt-1 block text-[11px] text-amber-700 dark:text-amber-400">
                  Activado por {cfg.directo_habilitado_por ?? '—'} el{' '}
                  {new Date(cfg.directo_habilitado_at).toLocaleString(LOCALE)}
                </span>
              )}
            </span>
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" /> Credenciales de la pasarela
        </CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Campo label="Proveedor">
            <select className={fieldCls} value={f.proveedor} onChange={(e) => set({ proveedor: e.target.value })}>
              {/* 903 · Se quitó «Simulado»: el worker sólo habla con PagoMedios,
                  así que esa opción ofrecía un botón que fallaba al pulsarlo. */}
              <option value="pagomedios">PagoMedios (Abitmedia)</option>
            </select>
          </Campo>
          <Campo label="Ambiente">
            <select className={fieldCls} value={f.ambiente} onChange={(e) => set({ ambiente: e.target.value })}>
              <option value="sandbox">Pruebas (sandbox)</option>
              <option value="produccion">Producción</option>
            </select>
          </Campo>
          <Campo label={cfg?.token_configurado ? 'Token (ya configurado — escribe uno nuevo para cambiarlo)' : 'Token'}>
            <input type="password" className={fieldCls} value={f.token} autoComplete="new-password"
                   placeholder={cfg?.token_configurado ? '••••••••' : 'Token de la pasarela'}
                   onChange={(e) => set({ token: e.target.value })} />
          </Campo>
          <Campo label="Forma de pago que dispara la pasarela">
            <select className={fieldCls} value={f.forma_pago_id} onChange={(e) => set({ forma_pago_id: e.target.value })}>
              <option value="">— Ninguna —</option>
              {formas.map((x) => <option key={x.id} value={x.id}>{x.codigo} · {x.nombre}</option>)}
            </select>
          </Campo>
          {/* Código SRI 20 («otros con utilización del sistema financiero»),
              distinto del 19 de la tarjeta de crédito: va en la factura. */}
          <Campo label="Forma de pago de De Una">
            <select className={fieldCls} value={f.forma_pago_deuna_id}
                    onChange={(e) => set({ forma_pago_deuna_id: e.target.value })}>
              <option value="">— Ninguna —</option>
              {formas.filter((x) => x.id !== f.forma_pago_id)
                .map((x) => <option key={x.id} value={x.id}>{x.codigo} · {x.nombre}</option>)}
            </select>
            {f.modo_deuna && !f.forma_pago_deuna_id && (
              <span className="mt-1 block text-[11px] text-pendiente">
                Sin una forma propia no se ofrecerá De Una: la factura declararía el código SRI de la tarjeta.
              </span>
            )}
          </Campo>
          <Campo label="URL base (opcional)">
            <input className={fieldCls} value={f.base_url} placeholder="https://api.abitmedia.cloud/pagomedios/v2"
                   onChange={(e) => set({ base_url: e.target.value })} />
          </Campo>
          <Campo label="Tiempo para pagar (segundos)">
            <input type="number" min={60} className={fieldCls} value={f.expira_segundos}
                   onChange={(e) => set({ expira_segundos: e.target.value })} />
          </Campo>
          <div className="md:col-span-2">
            <Switch checked={f.es_activo} onChange={(v) => set({ es_activo: v })}
                    label="Pasarela activa en este módulo" />
          </div>
        </CardContent>
      </Card>

      {sinToken && (
        <p className="flex items-center gap-2 text-sm text-amber-600">
          <AlertTriangle className="h-4 w-4" />
          Sin token, los modos de pasarela no se ofrecerán en la caja aunque queden encendidos.
        </p>
      )}

      <div className="flex justify-end">
        <Button isLoading={guardar.isPending} onClick={() => guardar.mutate()}>
          Guardar configuración de {MODULOS.find((m) => m.id === modulo)?.label}
        </Button>
      </div>
    </div>
  );
}
